/**
 * Persian (فارسی) — default language of the Xerex AI administration panel.
 *
 * Rules (PROMPT.md section 14):
 *  - This file is the ONLY place in the frontend that contains Persian UI copy.
 *  - Terminology follows the authoritative glossary in PROMPT.md 14.9.
 *  - No Finglish: «ارائه‌دهنده» — never «Provider» inside a Persian sentence.
 *  - Product, model and provider names (Gemini, DeepSeek, OpenAI, Claude, Qwen)
 *    are never translated; they are rendered inside LTR isolation containers.
 *  - Keys are semantic paths, never sentences.
 */
export const fa = {
  common: {
    appName: "Xerex AI",
    panel: "پنل مدیریت",
    loading: "در حال بارگذاری…",
    saving: "در حال ذخیره…",
    noData: "داده‌ای برای نمایش وجود ندارد",
    notAvailable: "نامشخص",
    unknown: "ناشناخته",
    never: "هرگز",
    yes: "بله",
    no: "خیر",
    all: "همه",
    none: "هیچ‌کدام",
    optional: "اختیاری",
    required: "الزامی",
    refreshing: "در حال به‌روزرسانی…",
    actions: { save: "ذخیره تغییرات", cancel: "لغو", delete: "حذف", edit: "ویرایش", details: "جزئیات", add: "افزودن", search: "جست‌وجو", filter: "فیلتر", export: "خروجی", retry: "تلاش مجدد", refresh: "به‌روزرسانی", copy: "رونوشت", copied: "رونوشت شد", close: "بستن", confirm: "تأیید", back: "بازگشت", showMore: "نمایش بیشتر", showLess: "نمایش کمتر", reset: "بازنشانی", apply: "اعمال", submit: "ثبت", view: "مشاهده", download: "دانلود", enable: "فعال‌سازی", disable: "غیرفعال‌سازی", select: "انتخاب کنید", copyFailed: "رونوشت انجام نشد" },
    status: { label: "وضعیت", enabled: "فعال", disabled: "غیرفعال", active: "فعال", inactive: "غیرفعال", pending: "در انتظار" },
    table: { empty: "موردی یافت نشد", loading: "در حال دریافت داده‌ها…", rowsPerPage: "تعداد در هر صفحه", page: "صفحه", of: "از", total: "مجموع", showingRange: "نمایش {{from}} تا {{to}} از {{total}} مورد", first: "نخست", previous: "قبلی", next: "بعدی", last: "آخر" },
    search: { placeholder: "جست‌وجو…", noResults: "نتیجه‌ای برای «{{query}}» یافت نشد" },
    confirmDialog: { title: "تأیید عملیات", defaultMessage: "آیا از انجام این عملیات مطمئن هستید؟", irreversible: "این عملیات قابل بازگشت نیست." },
    toast: { success: "عملیات با موفقیت انجام شد", error: "عملیات انجام نشد", copied: "در حافظه موقت رونوشت شد" },
    time: { justNow: "همین حالا", secondsAgo: "{{count}} ثانیه پیش", minutesAgo: "{{count}} دقیقه پیش", hoursAgo: "{{count}} ساعت پیش", daysAgo: "{{count}} روز پیش", never: "تا کنون انجام نشده" },
    units: { ms: "میلی‌ثانیه", seconds: "ثانیه", requests: "درخواست", tokens: "توکن", providers: "ارائه‌دهنده", models: "مدل", keys: "کلید", percent: "درصد" },
    technical: { label: "فنی", copyValue: "رونوشت مقدار", showRaw: "نمایش داده خام", rawData: "داده خام", identifiers: "شناسه‌های فنی" },
    language: { label: "زبان", fa: "فارسی", en: "English", switchTo: "تغییر زبان به {{language}}" },
    theme: { label: "پوسته", light: "روشن", dark: "تیره", system: "هماهنگ با سیستم" },
    build: { version: "نسخه", milestone: "گام", environment: "محیط", serverTime: "زمان سرور", milestoneValue: "گام {{milestone}}", footer: "Xerex AI — گام {{milestone}} · نسخه {{version}}" },
  },

  nav: {
    sectionOverview: "نمای کلی",
    sectionGateway: "دروازه هوش مصنوعی",
    sectionInsights: "پایش و مصرف",
    sectionAdministration: "مدیریت",
    dashboard: "داشبورد",
    providers: "ارائه‌دهندگان",
    credentials: "اطلاعات احراز هویت",
    models: "مدل‌ها",
    apiKeys: "کلیدهای API",
    health: "سلامت سیستم",
    routing: "مسیریابی",
    usage: "مصرف",
    logs: "گزارش‌ها",
    users: "کاربران",
    auditLogs: "گزارش رویدادها",
    settings: "تنظیمات",
    benchmark: "ارزیابی مدل‌ها",
    tools: "ابزارها",
    toolExecutions: "اجرای ابزارها",
    collapse: "جمع کردن منو",
    expand: "باز کردن منو",
    openMenu: "باز کردن منوی ناوبری",
    home: "خانه",
  },

  layout: {
    breadcrumbHome: "داشبورد",
    userMenu: { profile: "حساب کاربری", role: "نقش", signOut: "خروج از حساب", signedInAs: "وارد شده با" },
    environmentNotice: "محیط {{environment}} — این نسخه برای ارزیابی و توسعه است.",
    notices: { title: "اعلان‌ها", empty: "اعلان تازه‌ای وجود ندارد" },
  },

  auth: {
    signInTitle: "ورود به پنل مدیریت",
    signInSubtitle: "برای مدیریت ارائه‌دهندگان و مدل‌ها وارد شوید.",
    email: "ایمیل",
    emailPlaceholder: "name@example.com",
    password: "گذرواژه",
    passwordPlaceholder: "گذرواژه خود را وارد کنید",
    signIn: "ورود",
    signingIn: "در حال ورود…",
    signOut: "خروج از حساب",
    bootstrapTitle: "راه‌اندازی اولیه",
    bootstrapSubtitle: "نخستین حساب مدیر (مالک) را بسازید تا مدیریت پنل آغاز شود.",
    bootstrapSubmit: "ساخت حساب مدیر",
    bootstrapping: "در حال ساخت حساب…",
    fullName: "نام و نام خانوادگی",
    fullNamePlaceholder: "مثلاً سارا محمدی",
    forgotPassword: "گذرواژه خود را فراموش کرده‌اید؟",
    forgotPasswordHint: "بازیابی گذرواژه در گام بعدی پیاده‌سازی می‌شود.",
    securityNote: "نشست شما با نشانه‌های امن و کوکی محافظت‌شده نگهداری می‌شود.",
    bootstrapDisabledNotice: "راه‌اندازی اولیه روی این استقرار غیرفعال است. برای ساخت نخستین حساب مدیر با مسئول سامانه تماس بگیرید.",
    sessionExpired: "نشست شما پایان یافت. لطفاً دوباره وارد شوید.",
    signedInAs: "وارد شده به عنوان {{email}}",
    passwordHint: "دست‌کم ۱۰ نویسه، ترکیبی از حرف و رقم.",
  },

  dashboard: {
    title: "داشبورد",
    subtitle: "نمای کلی وضعیت سامانه در بازه {{window}} گذشته.",
    windowLabel: "بازه زمانی",
    sectionKpi: "شاخص‌های کلیدی",
    sectionProviderHealth: "وضعیت ارائه‌دهندگان",
    sectionTopModels: "پرمصرف‌ترین مدل‌ها",
    sectionModelPerformance: "گزارش عملکرد مدل‌ها",
    sectionPlatformState: "وضعیت راه‌اندازی سامانه",
    modelPerf: {
      model: "مدل",
      provider: "ارائه‌دهنده",
      samples: "نمونه",
      successRate: "موفقیت",
      latency: "تأخیر",
      timeoutRate: "timeout",
      probe: "probe",
      responding: "پاسخ‌گو",
      score: "امتیاز",
      yes: "بله",
      no: "خیر",
      coldStart: "شروع سرد",
      empty: "هنوز مدل فعالی ثبت نشده است",
      emptyHint: "ارائه‌دهنده اضافه و مدل‌ها را کشف کنید تا گزارش عملکرد نمایش یابد.",
      weightsNote: "وزن‌ها از تنظیمات → وزن‌دهی مسیریاب هوشمند.",
      windowNote: "بازه: {{window}}",
    },
    kpi: {
      requests: "درخواست‌ها",
      tokens: "مصرف توکن",
      cost: "هزینه",
      p95Latency: "زمان پاسخ صدک ۹۵",
      errorRate: "درصد خطا",
      activeProviders: "ارائه‌دهندگان فعال",
      noSamples: "نمونه‌ای ثبت نشده",
    },
    empty: {
      providers: "هنوز ارائه‌دهنده‌ای افزوده نشده است",
      providersHint: "برای دیدن آمار واقعی، در گام بعدی یک ارائه‌دهنده اضافه کنید و مدل‌های آن را کشف کنید.",
      models: "مدلی ثبت نشده است",
      modelsHint: "پس از افزودن ارائه‌دهنده، از بخش «مدل‌ها» گزینه «کشف مدل‌ها» را اجرا کنید.",
      usage: "ترافیکی ثبت نشده است",
      usageHint: "آمار مصرف پس از ارسال نخستین درخواست به دروازه هوش مصنوعی نمایش داده می‌شود.",
      noModels: "هنوز مدلی با آمار عملکرد ثبت نشده است",
      noModelsHint: "probe هر ساعت اجرا می‌شود؛ آمار ترافیک پس از درخواست‌های واقعی می‌آید.",
    },
    table: {
      rank: "#",
      model: "مدل",
      provider: "ارائه‌دهنده",
      successRate: "موفقیت",
      latency: "میانگین تأخیر",
      timeoutRate: "timeout",
      probe: "probe",
      score: "امتیاز",
      responding: "پاسخ‌گو",
      samples: "نمونه",
    },
    platform: {
      adminUsers: "کاربران مدیر",
      apiKeys: "کلیدهای API فعال",
      providers: "ارائه‌دهندگان ثبت‌شده",
      models: "مدل‌های ثبت‌شده",
    },
    honestyNote: "همه اعداد این صفحه از داده واقعی پایگاه داده خوانده می‌شوند؛ تا زمانی که ارائه‌دهنده‌ای متصل نشده باشد، مقدار آن‌ها صفر است.",
  },

  health: {
    title: "سلامت سیستم",
    subtitle: "بررسی زندهٔ پایگاه داده، Redis و زمان اجرای سرویس.",
    overall: "وضعیت کلی",
    components: "اجزای سامانه",
    component: "جزء",
    componentNames: { database: "پایگاه داده", redis: "Redis", api: "سرویس API" },
    latency: "زمان پاسخ",
    version: "نسخه",
    dialect: "نوع پایگاه داده",
    lastCheck: "آخرین بررسی",
    checkedAt: "زمان بررسی",
    checkNow: "بررسی سلامت",
    checking: "در حال بررسی…",
    required: "الزامی",
    optional: "اختیاری",
    degradedHint: "Redis در دسترس نیست. سامانه با حالت کاهش‌یافته کار می‌کند و محدودیت نرخ مؤقتاً غیرفعال است.",
    downtimeHint: "یک جزء الزامی از کار افتاده است؛ درخواست‌های مدیریتی ممکن است ناموفق باشند.",
    runtime: "زمان اجرا",
    serviceInfo: "مشخصات سرویس",
    milestones: { milestone: "گام پیاده‌سازی", environment: "محیط اجرا", startedAt: "زمان راه‌اندازی" },
    monitoringNote: "پایش پیوستهٔ ارائه‌دهندگان، مدل‌ها و نقاط اتصال در گام M5 فعال می‌شود.",
    providers: {
      title: "سلامت ارائه‌دهندگان",
      subtitle: "آخرین مشاهده هر ارائه‌دهنده، زمان پاسخ، نرخ خطا و در دسترس بودن ۲۴ ساعت گذشته.",
      errorRate: "نرخ خطا",
      requests: "درخواست (۲۴ ساعت)",
      empty: "ارائه‌دهنده‌ای ثبت نشده است",
      emptyHint: "پس از افزودن ارائه‌دهنده، نتیجه بررسی سلامت آن اینجا نمایش داده می‌شود.",
    },
    run: {
      button: "اجرای بررسی",
      done: "بررسی {{checked}} ارائه‌دهنده انجام شد",
      withFailures: "{{failed}} مورد نیازمند بررسی است",
    },
    history: {
      title: "تاریخچه بررسی‌ها",
      subtitle: "هر ردیف یک مشاهده ثبت‌شده است؛ مقدارها دست‌کاری نمی‌شوند.",
      filter: "فیلتر ارائه‌دهنده",
      allProviders: "همه ارائه‌دهندگان",
      empty: "بررسی‌ای ثبت نشده است",
      emptyHint: "با «اجرای بررسی» نخستین مشاهده‌ها ثبت می‌شوند.",
    },
    uptime: "در دسترس بودن (۲۴ ساعت)",
  },

  users: {
    title: "کاربران",
    subtitle: "مدیریت حساب‌های مدیر سامانه و نقش‌های دسترسی.",
    addUser: "افزودن کاربر",
    createTitle: "کاربر مدیر جدید",
    createSubtitle: "برای همکار خود حساب بسازید و نقش مناسب را تعیین کنید.",
    tableEmail: "ایمیل",
    tableName: "نام و نام خانوادگی",
    tableRole: "نقش",
    tableStatus: "وضعیت",
    tableLastLogin: "آخرین ورود",
    tableCreatedAt: "تاریخ ایجاد",
    tableActions: "عملیات",
    roleLabel: "نقش",
    statusLabel: "وضعیت",
    createdAt: "تاریخ ایجاد",
    lastLoginAt: "آخرین ورود",
    mfaEnabled: "ورود دو مرحله‌ای",
    editTitle: "ویرایش کاربر",
    updated: "تغییرات کاربر ذخیره شد",
    created: "کاربر مدیر ساخته شد",
    passwordLabel: "گذرواژه نخستین",
    passwordHint: "دست‌کم ۱۰ نویسه.",
    roles: { owner: "مالک", admin: "مدیر", operator: "اپراتور", viewer: "بازدیدکننده" },
    roleHints: { owner: "دسترسی کامل به همه بخش‌ها و تیم", admin: "مدیریت ارائه‌دهندگان، مدل‌ها و کاربران", operator: "کار عملیاتی مانند کشف مدل‌ها و بررسی سلامت", viewer: "فقط مشاهدهٔ اطلاعات" },
    statuses: { active: "فعال", suspended: "غیرفعال", invited: "دعوت‌شده" },
    selfNote: "حساب خودتان را نمی‌توانید غیرفعال کنید.",
    empty: "هنوز کاربر دیگری ساخته نشده است",
  },

  auditLogs: {
    title: "گزارش رویدادها",
    subtitle: "هر تغییر مدیریتی با شناسه درخواست ثبت می‌شود.",
    tableTime: "زمان",
    tableActor: "کاربر",
    tableAction: "رویداد",
    tableEntity: "موجودیت",
    tableIp: "نشانی IP",
    tableRequestId: "شناسه درخواست",
    tableDetails: "جزئیات",
    actions: { login_succeeded: "ورود موفق", login_failed: "ورود ناموفق", logout: "خروج از حساب", token_refreshed: "تمدید نشست", bootstrap_owner_created: "ساخت مالک اولیه", settings_updated: "تغییر تنظیمات", user_created: "ساخت کاربر", user_updated: "ویرایش کاربر", user_disabled: "غیرفعال‌سازی کاربر", provider_created: "افزودن ارائه‌دهنده", provider_updated: "ویرایش ارائه‌دهنده", provider_deleted: "حذف ارائه‌دهنده", provider_tested: "آزمایش اتصال ارائه‌دهنده", credential_created: "افزودن اطلاعات احراز هویت", credential_updated: "ویرایش اطلاعات احراز هویت", credential_deleted: "حذف اطلاعات احراز هویت", credential_rotated: "چرخش کلید دسترسی", credential_verified: "بررسی اعتبار کلید", credentials_exported: "دریافت خروجی کلیدهای دسترسی", model_created: "افزودن مدل", model_updated: "ویرایش مدل", model_deleted: "حذف مدل", models_discovered: "کشف مدل‌ها", endpoint_created: "افزودن نقطه اتصال مدل", endpoint_updated: "ویرایش نقطه اتصال مدل", endpoint_deleted: "حذف نقطه اتصال مدل", model_tested: "آزمایش مدل" },
    empty: "رویدادی ثبت نشده است",
    detailTitle: "جزئیات رویداد",
  },

  settings: {
    title: "تنظیمات",
    subtitle: "پیکربندی ظاهر، زبان و سیاست نگهداری داده‌های پنل.",
    sectionBranding: "برندینگ و هویت",
    sectionPresentation: "نمایش و زبان",
    sectionNotifications: "اعلان‌ها",
    sectionRetention: "نگهداری داده‌ها",
    sectionRouter: "وزن‌دهی مسیریاب هوشمند",
    sectionEnvironment: "پیکربندی محیط",
    saveSuccess: "تنظیمات ذخیره شد",
    saveError: "ذخیره تنظیمات انجام نشد",
    readOnlyBadge: "از طریق متغیرهای محیطی",
    savedAt: "آخرین تغییر: {{time}}",
    notSavedYet: "هنوز تغییری ثبت نشده است",
    fields: {
      "branding.name": { label: "نام دستیار", hint: "نامی که در پاسخ‌های چت به کاربران نمایش داده می‌شود." },
      "branding.system_prompt": { label: "پرامپت سیستم", hint: "دستوراتی که هویت و رفتار دستیار را تعریف می‌کنند." },
      "branding.reveal_model": { label: "نمایش نام مدل", hint: "نمایش نام مدل اصلی در پاسخ‌های API." },
      "ui.default_locale": { label: "زبان پیش‌فرض پنل", hint: "زبان پیش‌فرض پنل مدیریت. فارسی زبان اصلی است." },
      "ui.numeral_style": { label: "سبک اعداد", hint: "اعداد فنی مانند شناسه‌ها و کلیدها همیشه با رقم لاتین نمایش داده می‌شوند." },
      "ui.theme": { label: "پوسته ظاهری", hint: "پوسته روشن، تیره یا هماهنگ با سامانهٔ کاربر." },
      "ui.timezone": { label: "منطقه زمانی", hint: "زمان نمایش داده‌ها در پنل بر پایه این منطقه زمانی است." },
      "ui.sidebar_collapsed": { label: "منوی جمع‌شده", hint: "منوی کناری به صورت پیش‌فرض جمع‌شده باز شود." },
      "notifications.email_enabled": { label: "اطلاع‌رسانی ایمیلی", hint: "ارسال رخدادهای مهم سامانه با ایمیل." },
      "notifications.health_alerts": { label: "هشدار سلامت", hint: "اطلاع‌رسانی هنگام قطع یا کاهش سلامت ارائه‌دهنده." },
      "retention.usage_days": { label: "نگهداری آمار مصرف (روز)", hint: "مدت نگهداری رکوردهای مصرف پیش از تجمیع و پاک‌سازی." },
      "retention.logs_days": { label: "نگهداری گزارش‌ها (روز)", hint: "مدت نگهداری گزارش درخواست‌ها و رویدادها." },
      "router.weight_performance": { label: "وزن عملکرد", hint: "سهم موفقیت و تأخیر واقعی در رتبه‌بندی مدل‌ها (۰ تا ۱)." },
      "router.weight_capability": { label: "وزن تطابق توانایی", hint: "سهم تطابق نوع وظیفه و پیچیدگی با مدل (۰ تا ۱)." },
      "router.weight_health": { label: "مقیاس امتیاز سلامت", hint: "مقیاس پاداش/جریمه سلامت probe و provider (۰ تا ۱)." },
      "router.weight_timeout": { label: "جریمه timeout", hint: "ضریب جریمه نرخ timeout برای درخواست‌های سنگین (۰ تا ۱)." },
      "router.perf_success_weight": { label: "وزن نرخ موفقیت", hint: "سهم نرخ موفقیت در امتیاز عملکرد (۰ تا ۱)." },
      "router.perf_speed_weight": { label: "وزن سرعت", hint: "سهم تأخیر در امتیاز عملکرد (۰ تا ۱)." },
    },
    numeralStyles: { persian: "ارقام فارسی (۱۲۳)", latin: "ارقام لاتین (123)" },
    environmentNotice: "این مقادیر از متغیرهای محیطی سرویس خوانده می‌شوند و از پنل قابل تغییر نیستند.",
    environmentKeys: { apiPrefix: "پیشوند API", defaultLocale: "زبان پیش‌فرض سرویس", locales: "زبان‌های پشتیبانی‌شده", timezone: "منطقه زمانی پیش‌فرض" },
  },

  roadmap: {
    badge: "در گام بعدی",
    title: "این بخش هنوز پیاده‌سازی نشده است",
    description: "برای پرهیز از نمایش داده نادرست، این صفحه در گام {{milestone}} پیاده‌سازی می‌شود. در ادامه قرارداد API برنامه‌ریزی‌شده آمده است.",
    plannedEndpoints: "قرارداد API این بخش",
    implementedModules: "بخش‌های آمادهٔ استفاده",
    noFakeData: "در این نسخه هیچ داده ساختگی نمایش داده نمی‌شود.",
    endpoint: { method: "روش", path: "مسیر", summary: "توضیح", milestone: "گام" },
    modules: {
      providers: { title: "ارائه‌دهندگان", description: "افزودن ارائه‌دهنده، تنظیم آدرس پایه، اولویت، وزن، مهلت زمانی و تلاش مجدد." },
      credentials: { title: "اطلاعات احراز هویت", description: "ذخیره رمزنگاری‌شدهٔ کلیدهای ارائه‌دهنده، بررسی اعتبار و چرخش کلید." },
      models: { title: "مدل‌ها", description: "کشف خودکار مدل‌ها، قیمت‌گذاری، پنجره زمینه و آزمایش مدل." },
      apiKeys: { title: "کلیدهای API", description: "صدور کلید با محدوده دسترسی، سهمیه و محدودیت نرخ؛ نمایش یک‌بارهٔ کلید." },
      routing: { title: "مسیریابی", description: "راهبردهای اولویت، وزن، گردشی، تأخیر، هزینه و جایگزینی همراه با شبیه‌سازی." },
      usage: { title: "مصرف", description: "آمار توکن، هزینه، زمان پاسخ و درصد خطا با فیلتر بازه تاریخ خورشیدی." },
      logs: { title: "گزارش‌ها", description: "گزارش درخواست‌ها با شناسه درخواست، ارائه‌دهنده، مدل و کد خطا." },
      gateway: { title: "دروازه هوش مصنوعی", description: "نقطه پایانی سازگار با OpenAI برای گفت‌وگو، جاسازی و فهرست مدل‌ها." },
    },
  },

  providers: {
    title: "ارائه‌دهندگان",
    subtitle: "سرویس‌های هوش مصنوعی متصل به دروازه را مدیریت کنید.",
    addProvider: "افزودن ارائه‌دهنده",
    exportConfig: {
      button: "خروجی JSON",
      title: "دریافت خروجی کامل پیکربندی",
      message: "همهٔ ارائه‌دهندگان به همراه آدرس پایه و کلیدهای دسترسی در یک فایل JSON دانلود می‌شود.",
      detailSecrets: "کلیدهای دسترسی در این فایل به‌صورت متن ساده و رمزگشایی‌شده ذخیره می‌شوند؛ آن را در جای امن نگه دارید.",
      detailAudit: "نام کاربری، نقش و نشانی IP شما همراه این درخواست در گزارش رویدادها ثبت می‌شود.",
      confirm: "دانلود خروجی",
      done: "فایل «{{filename}}» دانلود شد.",
      failed: "دریافت خروجی ناموفق بود.",
    },
    createTitle: "افزودن ارائه‌دهنده",
    createSubtitle: "اطلاعات اتصال سرویس را وارد کنید. اطلاعات احراز هویت جداگانه ثبت می‌شود.",
    editTitle: "ویرایش ارائه‌دهنده",
    editSubtitle: "تغییرات پس از ذخیره در گزارش رویدادها ثبت می‌شود.",
    empty: "هنوز ارائه‌دهنده‌ای ثبت نشده است",
    emptyHint: "پس از افزودن ارائه‌دهنده و ثبت اطلاعات احراز هویت، می‌توانید مدل‌ها را کشف کنید.",
    searchPlaceholder: "جست‌وجوی نام یا شناسه…",
    filterKind: "نوع",
    filterState: "وضعیت",
    orderBy: "ترتیب",
    allKinds: "همه انواع",
    allStates: "همه وضعیت‌ها",
    sortPriority: "اولویت",
    sortName: "نام",
    sortNewest: "تازه‌ترین",
    clearFilters: "پاک کردن فیلترها",
    table: {
      name: "نام",
      kind: "نوع",
      baseUrl: "آدرس پایه",
      credentials: "اطلاعات احراز هویت",
      models: "مدل‌ها",
      health: "سلامت",
      priority: "اولویت",
      state: "وضعیت",
      updatedAt: "آخرین تغییر",
      actions: "عملیات",
    },
    test: {
      action: "آزمایش اتصال",
      running: "در حال آزمایش…",
      ok: "اتصال با موفقیت برقرار شد.",
      failed: "اتصال برقرار نشد.",
      latency: "زمان پاسخ",
      statusCode: "کد وضعیت",
      models: "مدل‌های شناسایی‌شده",
      needCredential: "برای آزمایش اتصال، نخست اطلاعات احراز هویت این ارائه‌دهنده را ثبت کنید.",
      title: "نتیجه آزمایش اتصال",
    },
    form: {
      sectionIdentity: "مشخصات",
      sectionConnection: "اتصال",
      sectionRouting: "مسیریابی و پایداری",
      name: "نام",
      nameHint: "نامی که در پنل نمایش داده می‌شود.",
      kind: "نوع",
      kindHint: "نوع سرویس، ساختار درخواست‌ها و روش احراز هویت را تعیین می‌کند.",
      baseUrl: "آدرس پایه",
      baseUrlHint: "نشانی پایه API؛ نمونه: https://api.openai.com/v1",
      slug: "شناسه",
      slugHint: "اختیاری؛ در صورت خالی ماندن از نام ساخته می‌شود.",
      description: "توضیحات",
      priority: "اولویت",
      priorityHint: "عدد کمتر به معنای اولویت بالاتر است.",
      weight: "وزن",
      weightHint: "در مسیریابی وزن‌دار به کار می‌رود.",
      timeout: "مهلت زمانی",
      timeoutHint: "حداکثر زمان انتظار برای هر درخواست، بر حسب میلی‌ثانیه.",
      maxRetries: "حداکثر تلاش مجدد",
      maxRetriesHint: "تعداد تلاش‌های مجدد پیش از جایگزینی با ارائه‌دهنده دیگر.",
      enabled: "فعال باشد",
      enabledHint: "ارائه‌دهنده غیرفعال در مسیریابی استفاده نمی‌شود.",
    },
    detail: {
      title: "جزئیات ارائه‌دهنده",
      back: "بازگشت به فهرست ارائه‌دهندگان",
      overview: "مشخصات",
      id: "شناسه فنی",
      slug: "شناسه",
      kind: "نوع",
      baseUrl: "آدرس پایه",
      health: "سلامت",
      priority: "اولویت",
      weight: "وزن",
      timeout: "مهلت زمانی",
      maxRetries: "حداکثر تلاش مجدد",
      credentials: "اطلاعات احراز هویت",
      models: "مدل‌ها",
      createdAt: "تاریخ ایجاد",
      updatedAt: "آخرین تغییر",
      description: "توضیحات",
    },
    delete: {
      title: "حذف ارائه‌دهنده",
      message: "آیا از حذف «{{name}}» مطمئن هستید؟",
      impact: "با این حذف، {{credentials}} اطلاعات احراز هویت و {{models}} مدل حذف می‌شود.",
      impactEndpoints: "{{endpoints}} نقطه اتصال مدل نیز از دسترس خارج می‌شود.",
      confirm: "حذف ارائه‌دهنده",
      done: "ارائه‌دهنده حذف شد",
    },
    created: "ارائه‌دهنده ثبت شد",
    updated: "تغییرات ذخیره شد",
    enabledDone: "ارائه‌دهنده فعال شد",
    disabledDone: "ارائه‌دهنده غیرفعال شد",
    notFound: "این ارائه‌دهنده یافت نشد.",
  },

  credentials: {
    title: "اطلاعات احراز هویت",
    subtitle: "کلیدهای دسترسی ارائه‌دهندگان را ثبت، بررسی و چرخش دهید.",
    selectProvider: "ارائه‌دهنده",
    selectProviderHint: "برای مدیریت اطلاعات احراز هویت، یک ارائه‌دهنده انتخاب کنید.",
    noProviders: "برای مدیریت اطلاعات احراز هویت، ابتدا یک ارائه‌دهنده اضافه کنید.",
    addCredential: "افزودن اطلاعات احراز هویت",
    createTitle: "افزودن اطلاعات احراز هویت",
    createSubtitle: "کلید دسترسی رمزنگاری‌شده ذخیره می‌شود و هرگز نمایش داده نخواهد شد.",
    empty: "برای این ارائه‌دهنده اطلاعات احراز هویتی ثبت نشده است",
    emptyHint: "تا زمانی که کلید معتبری ثبت نشود، آزمایش اتصال و کشف مدل‌ها ممکن نیست.",
    table: {
      label: "برچسب",
      status: "وضعیت",
      key: "کلید",
      lastVerified: "آخرین بررسی",
      lastError: "آخرین خطا",
      createdAt: "تاریخ ثبت",
    },
    label: "برچسب",
    labelHint: "نامی برای تشخیص این کلید، مثلاً «کلید اصلی».",
    secret: "کلید دسترسی",
    secretHint: "کلید فقط هنگام ثبت دریافت می‌شود و رمزنگاری‌شده ذخیره می‌شود.",
    secretPlaceholder: "sk-…",
    secretKeepHint: "کلیدهای ذخیره‌شده خوانده نمی‌شوند؛ برای تغییر کلید از «چرخش کلید» استفاده کنید.",
    verify: "بررسی اعتبار",
    verifying: "در حال بررسی…",
    verifyOk: "کلید معتبر است.",
    verifyFailed: "کلید معتبر نیست.",
    rotate: "چرخش کلید",
    rotateTitle: "چرخش کلید",
    rotateSubtitle: "کلید تازه جایگزین کلید فعلی می‌شود و وضعیت کلید به «بررسی‌نشده» بازمی‌گردد.",
    newSecret: "کلید تازه",
    revoke: "ابطال",
    revokeConfirm: "آیا از ابطال این کلید مطمئن هستید؟ تا زمانی که کلید تازه‌ای ثبت نشود، مسیریابی با این ارائه‌دهنده ممکن نیست.",
    revokeMessage: "آیا کلید «{{label}}» باطل شود؟",
    delete: {
      title: "حذف اطلاعات احراز هویت",
      message: "آیا از حذف «{{label}}» مطمئن هستید؟",
      boundEndpoints: "{{count}} نقطه اتصال مدل به این کلید وابسته است و پس از حذف از کلید پیش‌فرض ارائه‌دهنده استفاده می‌کند.",
      confirm: "حذف کلید",
      done: "اطلاعات احراز هویت حذف شد",
    },
    defaultCredential: "کلید پیش‌فرض ارائه‌دهنده",
    noDefaultCredential: "کلید پیش‌فرضی تعیین نشده است",
    created: "اطلاعات احراز هویت ثبت شد",
    updated: "تغییرات ذخیره شد",
    rotated: "کلید چرخش یافت",
    verified: "بررسی کلید انجام شد",
    revoked: "کلید باطل شد",
    statusUnverified: "پس از هر تغییر کلید، برای اطمینان از اعتبار آن «بررسی اعتبار» را اجرا کنید.",
  },

  models: {
      title: "مدل‌ها",
      subtitle: "مدل‌های در دسترس هر ارائه‌دهنده را مدیریت و آزمایش کنید.",
      addModel: "افزودن مدل",
      createTitle: "افزودن مدل",
      createSubtitle: "مدلی را که ارائه‌دهنده سرو می‌کند به‌صورت دستی ثبت کنید.",
      editTitle: "ویرایش مدل",
      editSubtitle: "قیمت‌گذاری، پنجره زمینه و وضعیت مدل را تغییر دهید.",
      empty: "هنوز مدلی ثبت نشده است",
      emptyHint: "می‌توانید مدل‌ها را از ارائه‌دهنده کشف کنید یا آن‌ها را دستی اضافه کنید.",
      searchPlaceholder: "جست‌وجوی نام مدل…",
      filterProvider: "ارائه‌دهنده",
      allProviders: "همه ارائه‌دهندگان",
      filterState: "وضعیت",
      allStates: "همه وضعیت‌ها",
      stateEnabled: "فعال",
      stateDisabled: "غیرفعال",
      stateDeprecated: "منسوخ",
      orderBy: "ترتیب",
      sortName: "نام",
      sortProvider: "ارائه‌دهنده",
      sortNewest: "تازه‌ترین",
      clearFilters: "پاک کردن فیلترها",
      discover: {
        action: "کشف مدل‌ها",
        title: "کشف مدل‌ها",
        subtitle: "فهرست مدل‌های ارائه‌دهنده خوانده و با فهرست فعلی هم‌سان می‌شود.",
        provider: "ارائه‌دهنده",
        overwrite: "به‌روزرسانی مدل‌های موجود",
        overwriteHint: "با فعال بودن این گزینه، اطلاعات مدل‌های تکراری با مقادیر تازه ارائه‌دهنده به‌روزرسانی می‌شود.",
        run: "شروع کشف",
        running: "در حال کشف مدل‌ها…",
        resultTitle: "نتیجه کشف مدل‌ها",
        discovered: "مدل یافت‌شده",
        created: "مدل تازه",
        updated: "به‌روزرسانی‌شده",
        skipped: "بدون تغییر",
        failed: "ناموفق",
        done: "کشف مدل‌ها انجام شد",
        needProvider: "برای کشف مدل‌ها ابتدا یک ارائه‌دهنده با اطلاعات احراز هویت ثبت کنید.",
      },
      table: {
        name: "نام مدل",
        provider: "ارائه‌دهنده",
        capabilities: "توانمندی‌ها",
        contextWindow: "پنجره زمینه",
        pricing: "قیمت‌گذاری",
        state: "وضعیت",
        discoveredAt: "تاریخ کشف",
        actions: "عملیات",
      },
      form: {
        name: "نام مدل نزد ارائه‌دهنده",
        nameHint: "همان شناسه‌ای که در درخواست‌ها استفاده می‌شود، مثلاً gpt-4o-mini",
        displayName: "نام نمایشی",
        displayNameHint: "نامی که در پنل نشان داده می‌شود.",
        provider: "ارائه‌دهنده",
        contextWindow: "پنجره زمینه",
        contextWindowHint: "بیشترین تعداد توکنی که مدل می‌تواند بپذیرد.",
        maxOutputTokens: "حداکثر توکن خروجی",
        inputPrice: "قیمت ورودی (هر یک میلیون توکن)",
        outputPrice: "قیمت خروجی (هر یک میلیون توکن)",
        pricingHint: "قیمت‌ها به دلار و بر پایه هر یک میلیون توکن ثبت می‌شوند؛ در صورت نامشخص بودن خالی بگذارید.",
        capabilities: "توانمندی‌ها",
        capabilitiesHint: "با کاما جدا کنید، مثلاً vision, tools",
        enabled: "فعال باشد",
        enabledHint: "مدل غیرفعال در مسیریابی و دروازه در دسترس نیست.",
        deprecated: "منسوخ شده است",
        deprecatedHint: "مدل منسوخ با هشدار نمایش داده می‌شود و به‌صورت پیش‌فرض انتخاب نمی‌شود.",
        sectionIdentity: "مشخصات مدل",
        sectionPricing: "قیمت‌گذاری",
        sectionState: "وضعیت",
      },
      detail: {
        title: "جزئیات مدل",
        back: "بازگشت به فهرست مدل‌ها",
        specs: "مشخصات",
        endpoints: "نقاط اتصال مدل",
        playground: "آزمایش مدل",
        provider: "ارائه‌دهنده",
        contextWindow: "پنجره زمینه",
        maxOutputTokens: "حداکثر توکن خروجی",
        inputPrice: "قیمت ورودی",
        outputPrice: "قیمت خروجی",
        capabilities: "توانمندی‌ها",
        discoveredAt: "تاریخ کشف",
        createdAt: "تاریخ ثبت",
        updatedAt: "آخرین تغییر",
        id: "شناسه فنی",
        noCapabilities: "توانمندی ثبت‌شده‌ای وجود ندارد",
        priceUnknown: "نامشخص",
        pricePerMillion: "${{price}} به‌ازای هر ۱M توکن",
      },
      endpoints: {
        empty: "برای این مدل نقطه اتصالی ثبت نشده است",
        emptyHint: "نقطه اتصال مسیر و روش فراخوانی مدل را تعیین می‌کند.",
        add: "افزودن نقطه اتصال",
        createTitle: "افزودن نقطه اتصال مدل",
        editTitle: "ویرایش نقطه اتصال مدل",
        path: "مسیر",
        pathHint: "مسیر نسبت به آدرس پایه ارائه‌دهنده، مثلاً /chat/completions",
        method: "روش",
        streaming: "پاسخ جریانی",
        streamingHint: "آیا این نقطه اتصال پاسخ جریانی می‌دهد؟",
        credential: "اطلاعات احراز هویت",
        credentialHint: "در صورت انتخاب، درخواست‌ها با این کلید ارسال می‌شود؛ در غیر این صورت از کلید پیش‌فرض ارائه‌دهنده استفاده می‌شود.",
        credentialDefault: "کلید پیش‌فرض ارائه‌دهنده",
        enabled: "فعال",
        table: {
          path: "مسیر",
          method: "روش",
          streaming: "جریانی",
          credential: "کلید",
          state: "وضعیت",
          actions: "عملیات",
        },
        created: "نقطه اتصال ثبت شد",
        updated: "تغییرات ذخیره شد",
        deleteTitle: "حذف نقطه اتصال مدل",
        deleteMessage: "آیا از حذف نقطه اتصال «{{path}}» مطمئن هستید؟",
        deleteConfirm: "حذف نقطه اتصال",
        deleted: "نقطه اتصال حذف شد",
      },
      playground: {
        title: "آزمایش مدل",
        subtitle: "یک درخواست واقعی به مدل بفرستید و پاسخ، زمان پاسخ و مصرف توکن را ببینید.",
        prompt: "متن درخواست",
        promptPlaceholder: "پرسش یا دستور خود را بنویسید…",
        temperature: "دما",
        temperatureHint: "عدد کمتر پاسخ‌های قطعی‌تر و عدد بیشتر پاسخ‌های خلاقانه‌تر تولید می‌کند.",
        maxTokens: "حداکثر توکن خروجی",
        stream: "پاسخ جریانی",
        run: "ارسال درخواست",
        running: "در حال اجرا…",
        streaming: "در حال دریافت پاسخ…",
        output: "پاسخ مدل",
        outputEmpty: "پاسخی دریافت نشد.",
        latency: "زمان پاسخ",
        inputTokens: "توکن ورودی",
        outputTokens: "توکن خروجی",
        totalTokens: "مجموع توکن",
        finishReason: "دلیل پایان",
        errorTitle: "درخواست ناموفق بود",
        streamUnsupported: "این مدل از پاسخ جریانی پشتیبانی نمی‌کند؛ پاسخ کامل دریافت شد.",
        needPrompt: "برای ارسال درخواست، متن آن را وارد کنید.",
        cost: "هزینه تخمینی",
        costHint: "بر پایه قیمت ثبت‌شده مدل محاسبه می‌شود؛ در صورت نبود قیمت نمایش داده نمی‌شود.",
        recentPrompt: "درخواست پیشین",
      },
      created: "مدل ثبت شد",
      updated: "تغییرات ذخیره شد",
      enabledDone: "مدل فعال شد",
      disabledDone: "مدل غیرفعال شد",
      deprecatedDone: "مدل منسوخ شد",
      restoredDone: "مدل از حالت منسوخ خارج شد",
      delete: {
        title: "حذف مدل",
        message: "آیا از حذف مدل «{{name}}» مطمئن هستید؟",
        impact: "با حذف این مدل، نقاط اتصال آن نیز حذف می‌شود.",
        confirm: "حذف مدل",
        done: "مدل حذف شد",
      },
  },

  apiKeys: {
    title: "کلیدهای API",
    subtitle: "کلیدهای دسترسی مصرف‌کنندگان را صادر، محدود و پایش کنید.",
    addKey: "صدور کلید",
    createTitle: "صدور کلید API",
    createSubtitle: "کلید پس از ثبت فقط یک‌بار نمایش داده می‌شود؛ آن را در جای امنی نگه دارید.",
    editTitle: "ویرایش کلید",
    editSubtitle: "نام، محدوده دسترسی و محدودیت‌ها را تغییر دهید. مقدار خود کلید تغییر نمی‌کند.",
    empty: "هنوز کلید API صادر نشده است",
    emptyHint: "برای اتصال سرویس‌های مصرف‌کننده به دروازه، نخستین کلید را صادر کنید.",
    searchPlaceholder: "جست‌وجوی نام یا پیشوند کلید…",
    filterState: "وضعیت",
    allStates: "همه وضعیت‌ها",
    stateActive: "فعال",
    stateDisabled: "غیرفعال",
    stateRevoked: "باطل‌شده",
    stateExpired: "منقضی‌شده",
    table: {
      name: "نام",
      key: "کلید",
      scopes: "محدوده دسترسی",
      rateLimit: "محدودیت نرخ",
      quota: "سهمیه",
      lastUsed: "آخرین استفاده",
      expiresAt: "انقضا",
      state: "وضعیت",
      createdAt: "تاریخ صدور",
      actions: "عملیات",
    },
    scope: { chat: "گفت‌وگو", embeddings: "برداری‌سازی", models: "مدل‌ها" },
    form: {
      name: "نام",
      nameHint: "نامی که در فهرست کلیدها نمایش داده می‌شود.",
      scopes: "محدوده دسترسی",
      scopesHint: "هر محدوده، دسترسی به بخشی از دروازه را ممکن می‌کند.",
      rateLimit: "محدودیت نرخ (در دقیقه)",
      rateLimitHint: "حداکثر درخواست در هر دقیقه برای این کلید.",
      quota: "سهمیه توکن",
      quotaHint: "اختیاری؛ پس از مصرف این مقدار، کلید پاسخ ۴۲۹ می‌گیرد.",
      expiresAt: "تاریخ انقضا",
      expiresAtHint: "اختیاری؛ پس از این تاریخ کلید کار نمی‌کند.",
      enabled: "فعال باشد",
    },
    secret: {
      title: "کلید ساخته شد",
      notice: "این کلید فقط همین یک‌بار نمایش داده می‌شود. پس از بستن این پنجره، مقدار آن قابل بازیابی نیست.",
      label: "کلید API",
      copied: "کلید رونوشت شد",
      done: "کلید را ذخیره کردم",
    },
    usage: {
      title: "مصرف کلید",
      sampleMessage: "سلام",
      requests: "درخواست‌ها",
      inputTokens: "توکن ورودی",
      outputTokens: "توکن خروجی",
      totalTokens: "مجموع توکن",
      cost: "هزینه",
      errors: "خطاها",
      quota: "سهمیه مصرف‌شده",
      remaining: "سهمیه باقی‌مانده",
      noQuota: "برای این کلید سهمیه‌ای تعیین نشده است",
    },
    actions: { usage: "مشاهده مصرف", revoke: "ابطال", enable: "فعال‌سازی", disable: "غیرفعال‌سازی" },
    revoke: {
      title: "ابطال کلید",
      message: "آیا از ابطال «{{name}}» مطمئن هستید؟",
      hint: "پس از ابطال، درخواست‌های ارسالی با این کلید پاسخ ۴۰۱ می‌گیرند، اما تاریخ مصرف آن حفظ می‌شود.",
      confirm: "ابطال کلید",
      done: "کلید باطل شد",
    },
    delete: {
      title: "حذف کلید",
      message: "آیا از حذف «{{name}}» مطمئن هستید؟",
      hint: "با حذف کلید، سابقه مصرف آن نیز حذف می‌شود. برای توقف دسترسی، «ابطال» گزینه بهتری است.",
      confirm: "حذف کلید",
      done: "کلید حذف شد",
    },
    created: "کلید صادر شد",
    updated: "تغییرات کلید ذخیره شد",
    enabledDone: "کلید فعال شد",
    disabledDone: "کلید غیرفعال شد",
    revokedNote: "این کلید باطل شده است و دیگر کار نمی‌کند.",
    expiredNote: "تاریخ انقضای این کلید گذشته است.",
    intro: {
      title: "چگونه استفاده کنیم؟",
      baseUrl: "آدرس پایه دروازه",
      hint: "کلید را در سرآیند Authorization با الگوی Bearer ارسال کنید. مسیرها با پیشوند /v1 در دسترس هستند.",
      endpoints: "مسیرهای در دسترس",
    },
  },
    api_key_not_found: "کلید API یافت نشد.",
    unknown_api_key_scope: "محدوده دسترسی انتخاب‌شده پشتیبانی نمی‌شود.",
    api_key_limit_reached: "حداکثر تعداد کلیدهای API ثبت شده است.",
    invalid_api_key: "کلید API نامعتبر، باطل‌شده یا منقضی است.",
    gateway_disabled: "دروازه روی این استقرار غیرفعال است.",
    quota_exceeded: "سهمیهٔ توکن این کلید به پایان رسیده است.",
    insufficient_scope: "این کلید اجازهٔ استفاده از این قابلیت را ندارد.",
    gateway_deadline_exceeded: "مهلت پردازش درخواست به پایان رسید.",
    gateway_no_streaming_candidate: "هیچ ارائه‌دهنده‌ای امکان پاسخ جریانی برای این مدل را ندارد.",
    invalid_request: "درخواست ارسالی نامعتبر است.",

  routing: {
    title: "مسیریابی هوشمند",
    subtitle: "راهبرد انتخاب ارائه‌دهنده را تعیین کنید و پیش از اعمال، نتیجه را شبیه‌سازی کنید.",
    strategiesTitle: "راهبردها",
    strategiesHint: "راهبرد پیش‌فرض هر مدل از قواعد زیر تعیین می‌شود؛ در نبود قاعده، «اولویت» اعمال می‌شود.",
    implemented: "پیاده‌سازی‌شده",
    columns: {
      strategy: "راهبرد",
      needs: "ورودی‌های موردنیاز",
      uses: "معیارهای تصمیم",
      summary: "توضیح",
    },
    needs: { priority: "اولویت ارائه‌دهنده", weight: "وزن ارائه‌دهنده", none: "بدون ورودی ویژه" },
    uses: {
      health: "سلامت",
      latency: "زمان پاسخ",
      cost: "هزینه",
      order: "ترتیب ثابت",
    },
    rules: {
      title: "قواعد مسیریابی",
      empty: "هنوز قاعده‌ای ثبت نشده است",
      emptyHint: "با افزودن قاعده می‌توانید برای یک مدل یا الگوی نام، راهبرد و زنجیره جایگزین تعیین کنید.",
      add: "افزودن قاعده",
      createTitle: "افزودن قاعده مسیریابی",
      editTitle: "ویرایش قاعده",
      createSubtitle: "قاعده فقط بر درخواست‌های منطبق با شرط‌های آن اثر می‌گذارد.",
      editSubtitle: "تغییرات پس از ذخیره بر درخواست‌های بعدی اعمال می‌شود.",
      name: "نام قاعده",
      modelPattern: "الگوی نام مدل",
      modelPatternHint: "مثال: gpt-4o* — خالی بگذارید تا قاعده بر همه مدل‌ها اعمال شود.",
      strategy: "راهبرد",
      priority: "اولویت قاعده",
      priorityHint: "عدد کمتر یعنی بررسی زودتر. نخستین قاعده منطبق انتخاب می‌شود.",
      enabled: "قاعده فعال باشد",
      table: { name: "نام", pattern: "شرط", strategy: "راهبرد", priority: "اولویت", enabled: "وضعیت", actions: "عملیات" },
      allModels: "همه مدل‌ها",
      delete: {
        title: "حذف قاعده",
        message: "آیا از حذف قاعده «{{name}}» مطمئن هستید؟",
        confirm: "حذف قاعده",
        done: "قاعده حذف شد",
      },
      created: "قاعده ثبت شد",
      updated: "تغییرات قاعده ذخیره شد",
      deleted: "قاعده حذف شد",
      enabledDone: "قاعده فعال شد",
      disabledDone: "قاعده غیرفعال شد",
    },
    simulate: {
      title: "شبیه‌سازی مسیریابی",
      subtitle: "هیچ درخواستی به ارائه‌دهنده ارسال نمی‌شود؛ فقط ترتیب انتخاب و دلیل آن نمایش داده می‌شود.",
      model: "مدل",
      modelPlaceholder: "نام دقیق مدل، مانند gpt-4o-mini",
      strategy: "راهبرد شبیه‌سازی",
      useRule: "راهبرد قاعده منطبق",
      tokens: "توکن تخمینی",
      tokensHint: "برای برآورد هزینه استفاده می‌شود و هیچ توکنی مصرف نمی‌کند.",
      run: "اجرای شبیه‌سازی",
      resultTitle: "ترتیب انتخاب",
      selected: "انتخاب نخست",
      eligible: "واجد شرایط",
      excluded: "کنارگذاشته‌شده",
      candidate: "گزینه",
      score: "امتیاز",
      latency: "زمان پاسخ مشاهده‌شده",
      price: "هزینه هر یک‌میلیون توکن",
      estimatedCost: "هزینه تخمینی",
      reasons: "دلیل‌ها",
      noCandidates: "هیچ ارائه‌دهنده فعالی برای این مدل پیدا نشد",
      noCandidatesHint: "نخست مدل را ثبت کنید و اطلاعات احراز هویت ارائه‌دهنده را ذخیره کنید.",
      explanation: "جزئیات تصمیم",
      empty: "برای دیدن ترتیب انتخاب، یک مدل را شبیه‌سازی کنید",
      emptyHint: "نام مدل را وارد کنید و «اجرای شبیه‌سازی» را بزنید.",
      failed: "شبیه‌سازی انجام نشد",
    },
    reason: {
      heading: "دلایل",
      provider_priority: "اولویت ارائه‌دهنده: {{value}}",
      weight: "وزن ارائه‌دهنده: {{value}}",
      health: "وضعیت سلامت: {{value}}",
      observed_latency_ms: "زمان پاسخ مشاهده‌شده: {{value}} میلی‌ثانیه",
      no_latency_sample: "نمونه‌ای از زمان پاسخ ثبت نشده است",
      blended_price: "هزینه یک‌میلیون توکن ورودی و خروجی: {{value}} دلار",
      no_price_data: "قیمت این مدل ثبت نشده است",
      excluded_health_down: "به دلیل قطع بودن ارائه‌دهنده کنار گذاشته شد",
      round_robin_turn: "نوبت این گزینه در چرخش است",
      round_robin_offset: "جایگاه چرخش: {{value}}",
      unknown: "{{value}}",
    },
  },

  benchmark: {
    title: "بنچمارک",
    subtitle: "کارایی اندازه‌گیری‌شده مدل‌ها بر پایه ترافیک واقعی",
    range: "بازه زمانی",
    days7: "۷ روز",
    days14: "۱۴ روز",
    days30: "۳۰ روز",
    days90: "۹۰ روز",
    kpiModels: "مدل ارزیابی‌شده",
    kpiRequests: "مجموع درخواست‌ها",
    kpiSuccess: "نرخ موفقیت",
    kpiLatency: "تأخیر میانگین",
    kpiRequestsHint: "مجموع درخواست‌های همه مدل‌ها در این بازه",
    kpiSuccessHint: "میانگین وزنی بر پایه تعداد درخواست هر مدل",
    kpiLatencyHint: "میانگین وزنی بر پایه تعداد درخواست هر مدل",
    chartScores: "امتیاز مدل‌ها",
    chartScoresHint: "امتیاز ترکیبی کارایی، از ۰ تا ۱",
    chartScoresEmpty: "برای این مدل‌ها امتیازی محاسبه نشده است",
    trend: "روند در طول زمان",
    trendHint: "روزانه، برای {count} مدل برتر",
    metricRequests: "تعداد درخواست",
    metricLatency: "تأخیر (میلی‌ثانیه)",
    trendEmpty: "برای این بازه داده روزانه‌ای وجود ندارد",
    trendMoreHidden: "{hidden} مدل دیگر فقط در جدول زیر آمده است",
    unitMs: "م‌ث",
    tableTitle: "جزئیات مدل‌ها",
    tableHint: "روی سرستون‌ها برای مرتب‌سازی کلیک کنید",
    colModel: "مدل",
    colProvider: "ارائه‌دهنده",
    colScore: "امتیاز",
    colSuccess: "نرخ موفقیت",
    colLatency: "تأخیر میانگین",
    colRequests: "درخواست‌ها",
    emptyTitle: "هنوز داده‌ای برای بنچمارک نیست",
    emptyHint: "پس از چند درخواست موفق، امتیاز کارایی مدل‌ها اینجا محاسبه می‌شود",
  },

  enums: {
    modelState: { enabled: "فعال", disabled: "غیرفعال", deprecated: "منسوخ" },
    apiKeyState: { active: "فعال", disabled: "غیرفعال", revoked: "باطل‌شده", expired: "منقضی‌شده" },
    providerKind: { openai: "OpenAI", anthropic: "Claude", google: "Gemini", deepseek: "DeepSeek", qwen: "Qwen", openai_compatible: "سازگار با OpenAI" },
    credentialStatus: { unverified: "بررسی‌نشده", active: "فعال", invalid: "نامعتبر", revoked: "باطل‌شده" },
    healthStatus: { healthy: "سالم", degraded: "کاهش‌یافته", rate_limited: "محدودشده با نرخ", down: "قطع", unknown: "نامشخص" },
    clientRequestState: { pending: "در انتظار", succeeded: "موفق", failed: "ناموفق" },
    strategy: { priority: "اولویت", weighted: "وزن‌دار", round_robin: "گردشی", latency_aware: "آگاه از تأخیر", cost_aware: "آگاه از هزینه", failover: "جایگزینی" },
    role: { owner: "مالک", admin: "مدیر", operator: "اپراتور", viewer: "بازدیدکننده" },
    userStatus: { active: "فعال", suspended: "غیرفعال", invited: "دعوت‌شده" },
    environment: { development: "توسعه", test: "آزمون", staging: "پیش‌تولید", production: "تولید" },
    capabilityState: { implemented: "پیاده‌سازی‌شده", placeholder: "جای‌نگهدار", planned: "برنامه‌ریزی‌شده" },
  },

  errors: {
    title: "خطایی رخ داد",
    generic: "انجام عملیات ممکن نشد. لطفاً دوباره تلاش کنید.",
    network: "ارتباط با سرور برقرار نشد. اتصال شبکه را بررسی کنید.",
    timeout: "پاسخی از سرور دریافت نشد. زمان درخواست به پایان رسید.",
    invalid_credentials: "ایمیل یا گذرواژه نادرست است.",
    authentication_required: "برای ادامه باید وارد حساب شوید.",
    authentication_failed: "احراز هویت انجام نشد.",
    token_expired: "نشست شما پایان یافته است. دوباره وارد شوید.",
    token_invalid: "نشانه دسترسی نامعتبر است.",
    token_type_invalid: "نوع نشانه دسترسی پذیرفته نمی‌شود.",
    token_revoked: "این نشست باطل شده است. دوباره وارد شوید.",
    refresh_token_missing: "نشست فعالی یافت نشد. وارد شوید.",
    account_inactive: "این حساب غیرفعال است. با مالک سامانه تماس بگیرید.",
    account_missing: "حساب کاربری یافت نشد.",
    insufficient_role: "نقش شما اجازهٔ انجام این کار را ندارد.",
    permission_denied: "دسترسی شما به این بخش مجاز نیست.",
    bootstrap_closed: "راه‌اندازی اولیه پیش‌تر انجام شده است.",
    bootstrap_disabled: "راه‌اندازی اولیه روی این استقرار غیرفعال است.",
    role_escalation_not_allowed: "فقط مالک سامانه می‌تواند نقش مالک را واگذار کند.",
    cannot_change_own_role: "نقش خودتان را نمی‌توانید تغییر دهید.",
    email_taken: "حسابی با این ایمیل از قبل وجود دارد.",
    owner_role_protected: "نقش مالک قابل تغییر نیست.",
    cannot_suspend_self: "نمی‌توانید حساب خودتان را غیرفعال کنید.",
    password_too_short: "گذرواژه باید دست‌کم {{min_length}} نویسه باشد.",
    unknown_setting: "این تنظیم شناخته‌شده نیست.",
    invalid_setting_value: "مقدار وارد‌شده برای این تنظیم معتبر نیست.",
    setting_read_only: "این تنظیم از پنل قابل تغییر نیست.",
    login_rate_limited: "تلاش‌های ورود بیش از حد مجاز بود. کمی بعد دوباره تلاش کنید.",
    rate_limit_exceeded: "تعداد درخواست‌ها بیش از حد مجاز است. کمی بعد تلاش کنید.",
    request_validation_failed: "برخی فیلدها نامعتبر هستند. مقدارها را بررسی کنید.",
    validation_failed: "داده‌های ارسالی نامعتبر است.",
    not_found: "موردی با این مشخصات یافت نشد.",
    route_not_found: "این مسیر در سرویس وجود ندارد.",
    conflict: "این عملیات با وضعیت فعلی سامانه تعارض دارد.",
    internal_error: "خطای غیرمنتظره‌ای در سرویس رخ داد.",
    dependency_unavailable: "یک سرویس وابسته در دسترس نیست.",
    component_down: "یک جزء ضروری سامانه از کار افتاده است.",
    not_implemented: "این قابلیت در گام‌های بعدی پیاده‌سازی می‌شود.",
    method_not_allowed: "این متد برای این مسیر مجاز نیست.",
    http_error: "سرویس پاسخ ناموفق برگرداند.",
    unknown_error: "خطای ناشناخته رخ داد.",

      model_not_found: "این مدل یافت نشد.",
      model_name_duplicate: "مدلی با همین نام برای این ارائه‌دهنده ثبت شده است.",
      endpoint_not_found: "نقطه اتصال مدل یافت نشد.",
      endpoint_path_invalid: "مسیر نقطه اتصال باید با «/» آغاز شود.",
      endpoint_of_another_model: "این نقطه اتصال به مدل دیگری تعلق دارد.",
      model_disabled: "این مدل غیرفعال است.",
    provider_not_found: "ارائه‌دهنده یافت نشد.",
    credential_not_found: "اطلاعات احراز هویت یافت نشد.",
    credential_label_duplicate: "برای این ارائه‌دهنده، کلیدی با همین برچسب ثبت شده است.",
    credential_decryption_failed: "خواندن کلید ذخیره‌شده ممکن نشد. کلید رمزنگاری سامانه را بررسی کنید.",
    base_url_required: "آدرس پایه برای این نوع ارائه‌دهنده الزامی است.",
    credential_missing: "اطلاعات احراز هویت برای این ارائه‌دهنده ثبت نشده است.",
    provider_error: "ارائه‌دهنده به درخواست پاسخ ناموفق داد.",
    provider_unauthorized: "کلید دسترسی پذیرفته نشد (HTTP 401).",
    provider_forbidden: "دسترسی این کلید به این عملیات مجاز نیست (HTTP 403).",
    provider_rate_limited: "ارائه‌دهنده درخواست‌ها را محدود کرده است (HTTP 429). کمی بعد تلاش کنید.",
    provider_timeout: "ارائه‌دهنده در مهلت زمانی تعیین‌شده پاسخ نداد.",
    provider_unavailable: "سرویس ارائه‌دهنده موقتاً در دسترس نیست.",
    provider_unreachable: "ارتباط شبکه‌ای با ارائه‌دهنده برقرار نشد.",
    provider_bad_request: "درخواست ارسالی برای ارائه‌دهنده نامعتبر بود.",
    provider_request_too_large: "حجم درخواست بیش از حد مجاز ارائه‌دهنده است.",
    provider_unprocessable: "ارائه‌دهنده داده‌های درخواست را نپذیرفت (HTTP 422).",
    provider_model_not_found: "این مدل نزد ارائه‌دهنده یافت نشد.",
    provider_unexpected_response: "پاسخ ارائه‌دهنده با قالب انتظارشده همخوان نبود.",
    provider_base_url_missing: "آدرس پایه ارائه‌دهنده تنظیم نشده است.",
    provider_discovery_unsupported: "این ارائه‌دهنده کشف خودکار مدل‌ها را پشتیبانی نمی‌کند.",
    provider_chat_unsupported: "این ارائه‌دهنده امکان گفت‌وگوی متنی را پشتیبانی نمی‌کند.",
    provider_streaming_unsupported: "این ارائه‌دهنده پاسخ جریانی را پشتیبانی نمی‌کند.",
    provider_embeddings_unsupported: "این ارائه‌دهنده بردار معنایی (embeddings) را پشتیبانی نمی‌کند.",
    requestId: "شناسه درخواست",
    technicalDetails: "جزئیات فنی",
    errorCode: "کد خطا",
    retry: "تلاش مجدد",
    returnToDashboard: "بازگشت به داشبورد",
  },

  validation: {
    required: "تکمیل این فیلد الزامی است.",
    email: "نشانی ایمیل معتبر نیست.",
    minLength: "این مقدار باید دست‌کم {{count}} نویسه باشد.",
    maxLength: "این مقدار باید حداکثر {{count}} نویسه باشد.",
    number: "یک عدد معتبر وارد کنید.",
    positiveNumber: "یک عدد مثبت وارد کنید.",
    url: "نشانی اینترنتی معتبر وارد کنید (با http یا https).",
    password: "گذرواژه باید دست‌کم ۱۰ نویسه باشد.",
  },

  usage: {
    title: "مصرف",
    subtitle: "آمار درخواست‌ها، توکن، هزینه، زمان پاسخ و درصد خطا را بر پایهٔ تلاش‌های واقعی به ارائه‌دهنده بررسی کنید.",
    filters: {
      title: "بازهٔ زمانی",
      datePlaceholder: "۱۴۰۵/۰۶/۲۸",
      from: "از تاریخ",
      to: "تا تاریخ",
      hint: "تاریخ‌ها خورشیدی‌اند؛ برای نمونه ۱۴۰۵/۰۶/۲۸. خالی گذاشتن بازه، هفت روز گذشته را نشان می‌دهد.",
      invalidDate: "تاریخ خورشیدی را به شکل ۱۴۰۵/۰۶/۲۸ وارد کنید.",
      presetToday: "امروز",
      preset7: "۷ روز گذشته",
      preset30: "۳۰ روز گذشته",
      preset90: "۹۰ روز گذشته",
      provider: "ارائه‌دهنده",
      model: "مدل",
      apiKey: "کلید API",
      all: "همه",
      apply: "اعمال فیلتر",
      reset: "پاک‌کردن فیلترها",
      activeRange: "بازهٔ انتخابی: {{range}}",
      interval: "تفکیک زمانی",
      intervalHour: "ساعتی",
      intervalDay: "روزانه",
      intervalWeek: "هفتگی",
      intervalMonth: "ماهانه",
    },
    totals: {
      requests: "درخواست‌ها",
      requestsHint: "شمار تلاش‌های ثبت‌شده در این بازه",
      tokens: "مجموع توکن",
      tokensHint: "ورودی و خروجی روی هم",
      cost: "هزینه",
      costHint: "بر پایهٔ قیمت‌گذاری هر مدل",
      latency: "میانگین زمان پاسخ",
      p95: "صدک ۹۵ زمان پاسخ",
      errorRate: "درصد خطا",
      errors: "خطاها",
      errorsHint: "تلاش‌های ناموفق",
      inputTokens: "توکن ورودی",
      outputTokens: "توکن خروجی",
    },
    breakdown: {
      title: "تفکیک مصرف",
      subtitle: "مصرف را بر پایهٔ ارائه‌دهنده، مدل یا کلید API مقایسه کنید.",
      dimension: "تفکیک بر اساس",
      byProvider: "ارائه‌دهنده",
      byModel: "مدل",
      byKey: "کلید API",
      label: "عنوان",
      requests: "درخواست‌ها",
      tokens: "توکن",
      cost: "هزینه",
      errors: "خطاها",
      errorRate: "درصد خطا",
      latency: "میانگین پاسخ",
      empty: "مصرفی برای این بازه ثبت نشده است",
      emptyHint: "با تغییر بازهٔ تاریخ یا پاک‌کردن فیلترها دوباره بررسی کنید.",
    },
    series: {
      title: "روند مصرف",
      subtitle: "هر ردیف یک بازهٔ زمانی در محدودهٔ انتخابی است.",
      bucket: "بازه",
      requests: "درخواست‌ها",
      tokens: "توکن",
      cost: "هزینه",
      errors: "خطا",
      latency: "میانگین زمان پاسخ",
      empty: "در این بازه داده‌ای برای نمایش نیست",
    },
    export: {
      csv: "برون‌بری CSV",
      json: "برون‌بری JSON",
      csvHint: "فایل CSV با UTF-8 و علامت BOM ساخته می‌شود تا در Excel باز شود.",
      jsonHint: "فایل JSON برای پردازش خودکار و اسکریپت‌ها مناسب است.",
      done: "فایل «{{name}}» آماده شد",
      failed: "برون‌بری ناموفق بود",
      rollups: "بازسازی تجمیع روزانه",
      rollupsHint: "تجمیع روزانه را از روی تلاش‌های ذخیره‌شده دوباره محاسبه می‌کند.",
      rollupsDone: "تجمیع روزانه بازسازی شد",
    },
    states: {
      noActivity: "ترافیکی برای این بازه ثبت نشده است",
      noActivityHint: "پس از نخستین درخواست موفق به دروازه، آمار مصرف همین‌جا ساخته می‌شود.",
      rangeNote: "بازهٔ داده‌ها: {{range}}",
      generatedAt: "زمان محاسبه: {{time}}",
    },
  },

  logs: {
    title: "گزارش درخواست‌ها",
    subtitle: "هر درخواست ارسالی به دروازه را با نتیجه، تلاش‌ها، توکن و زمان پاسخ بررسی کنید.",
    filters: {
      title: "فیلترها",
      datePlaceholder: "۱۴۰۵/۰۶/۲۸",
      search: "جست‌وجو",
      searchPlaceholder: "شناسهٔ درخواست، ارائه‌دهنده، مدل یا کد خطا",
      state: "وضعیت",
      allStates: "همهٔ وضعیت‌ها",
      errorCode: "کد خطا",
      errorCodePlaceholder: "برای نمونه provider_unavailable",
      dateFrom: "از تاریخ",
      dateTo: "تا تاریخ",
      dateHint: "تاریخ خورشیدی (۱۴۰۵/۰۶/۲۸)؛ خالی یعنی بدون محدودیت.",
      invalidDate: "تاریخ خورشیدی را به شکل ۱۴۰۵/۰۶/۲۸ وارد کنید.",
      provider: "ارائه‌دهنده",
      model: "مدل",
      all: "همه",
      apply: "اعمال فیلتر",
      reset: "پاک‌کردن فیلترها",
    },
    table: {
      time: "زمان",
      requestId: "شناسهٔ درخواست",
      key: "کلید API",
      provider: "ارائه‌دهنده",
      model: "مدل",
      state: "وضعیت",
      attempts: "تلاش‌ها",
      statusCode: "کد وضعیت",
      tokens: "توکن",
      latency: "زمان پاسخ",
      details: "جزئیات",
    },
    state: {
      succeeded: "موفق",
      failed: "ناموفق",
      pending: "در جریان",
    },
    streaming: "جریانی",
    attemptsCount: "{{count}} تلاش",
    empty: "درخواستی با این فیلترها پیدا نشد",
    emptyHint: "بازهٔ تاریخ، وضعیت یا عبارت جست‌وجو را تغییر دهید.",
    pagination: {
      previous: "قبلی",
      next: "بعدی",
      page: "صفحهٔ {{page}} از {{total}}",
      total: "{{count}} درخواست",
    },
    detail: {
      title: "جزئیات درخواست",
      subtitle: "شناسهٔ {{requestId}}",
      failureOnly: "تنها درخواست‌های ناموفق جزئیات دارند؟",
      summary: "خلاصهٔ درخواست",
      state: "وضعیت",
      statusCode: "کد وضعیت",
      errorCode: "کد خطا",
      attempts: "تلاش‌های ارسال",
      attemptsHint: "هر تلاش یک فراخوانی واقعی به ارائه‌دهنده است؛ ترتیب آن داستان جایگزینی (failover) را نشان می‌دهد.",
      attempt: "تلاش {{number}}",
      finalAttempt: "تلاش پایانی",
      retryableAttempt: "قابل تلاش مجدد",
      provider: "ارائه‌دهنده",
      model: "مدل",
      credential: "اطلاعات احراز هویت",
      endpoint: "نقطه اتصال",
      tokens: "توکن (ورودی/خروجی)",
      cost: "هزینه",
      latency: "زمان پاسخ",
      raw: "داده خام (JSON)",
      rawHint: "برای ارجاع فنی و پیگیری خطا؛ خروجی همیشه چپ‌به‌راست نمایش داده می‌شود.",
      copy: "رونوشت JSON",
      copied: "رونوشت شد",
      noAttempts: "برای این درخواست تلاشی ثبت نشده است",
      loadFailed: "دریافت جزئیات درخواست ناموفق بود",
    },
  },

  tools: {
    title: "ابزارها",
    subtitle: "مدیریت تعاریف و مجوزهای ابزارهای دروازه.",
    addTool: "افزودن ابزار",
    createTitle: "ثبت ابزار جدید",
    listTitle: "ابزارهای ثبت‌شده",
    empty: "ابزاری ثبت نشده",
    emptyHint: "ابزاری ثبت کنید تا اجرای ابزار بومی دروازه فعال شود.",
    createDone: "ابزار ایجاد شد",
    createError: "ایجاد ابزار ناموفق بود",
    toggleDone: "ابزار به‌روزرسانی شد",
    deleteDone: "ابزار حذف شد",
    confirmDelete: "ابزار \"{{name}}\" حذف شود؟",
    table: { name: "نام", type: "نوع", policy: "سیاست", timeout: "مهلت", status: "وضعیت", actions: "عملیات" },
    form: { name: "نام ابزار", description: "توضیحات", schema: "ساختار ورودی (JSON)" },
  },

  toolExecutions: {
    title: "اجرای ابزارها",
    subtitle: " ردیابی حسابرسی هر فراخوانی ابزار از طریق دروازه.",
    listTitle: "آخرین اجراها",
    empty: "هنوز اجرای ابزاری ثبت نشده",
    emptyHint: "اجراهای ابزار پس از اولین فراخوانی ابزار از طریق دروازه اینجا نمایش داده می‌شوند.",
    table: { time: "زمان", tool: "ابزار", controller: "کنترلر", status: "وضعیت", latency: "تأخیر", iteration: "تکرار", requestId: "درخواست" },
  },

  notFound: {
    title: "صفحه یافت نشد",
    description: "مسیر درخواستی در پنل مدیریت وجود ندارد.",
    action: "بازگشت به داشبورد",
  },

  states: {
    loadingTitle: "در حال دریافت اطلاعات",
    errorTitle: "دریافت اطلاعات ناموفق بود",
    emptyTitle: "موردی برای نمایش نیست",
    retry: "تلاش مجدد",
  },
} as const;

/**
 * Shape shared by every locale: identical key paths, widened string values, so a
 * translation file can provide English text without literal-type conflicts.
 */
type Widen<T> = {
  [K in keyof T]: T[K] extends string ? string : Widen<T[K]>;
};

export type FaResources = Widen<typeof fa>;
export default fa;
